import axios from "axios";
import config from "./config";

const USE_MOCK_BY_DEFAULT = false;

const http = axios.create({
    baseURL: config.host + "/api",
});

http.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    } else {
        delete config.headers.Authorization;
    }

    return config;
});

const mockCompanies = [
    { id: "c1", name: 'ООО "Альфа"' },
    { id: "c2", name: 'ООО "Бета"' },
    { id: "c3", name: 'АО "Вектор"' },
];

const mockFile = (id, name) => ({
    id,
    name,
    url: "#",
});

let mockRequests = [
    {
        id: "r1",
        date: "2026-01-10",
        number: "REQ-001",
        companyId: "c1",
        companyName: 'ООО "Альфа"',
        kmocDocuments: [mockFile("f1", "kmoc-contract.pdf")],
        curatorDocuments: [mockFile("f2", "curator-note.docx"), mockFile("f3", "report.pdf")],
        originalSent: "Почта РФ, трек 1111",
        status: "new",
        updatedAt: "2026-01-11 10:30",
    },
    {
        id: "r2",
        date: "2026-01-14",
        number: "REQ-002",
        companyId: "c2",
        companyName: 'ООО "Бета"',
        kmocDocuments: [mockFile("f4", "kmoc-passport.pdf"), mockFile("f5", "kmoc-license.pdf")],
        curatorDocuments: [mockFile("f6", "curator-checklist.xlsx")],
        originalSent: "",
        status: "in_progress",
        updatedAt: "2026-01-16 18:00",
    },
];

const statuses = [
    { label: "Новая", value: "new" },
    { label: "В обработке", value: "in_progress" },
    { label: "Закрыта", value: "done" },
];

const byDateRange = (value, dateFrom, dateTo) => {
    if (!value) return true;
    const val = new Date(value).getTime();
    if (dateFrom && val < new Date(dateFrom).getTime()) return false;
    return !(dateTo && val > new Date(dateTo).getTime());
};

const sorters = {
    date_asc: (a, b) => String(a.date).localeCompare(String(b.date)),
    date_desc: (a, b) => String(b.date).localeCompare(String(a.date)),
    number_asc: (a, b) => String(a.number).localeCompare(String(b.number)),
    number_desc: (a, b) => String(b.number).localeCompare(String(a.number)),
    company_asc: (a, b) => String(a.companyName).localeCompare(String(b.companyName)),
    company_desc: (a, b) => String(b.companyName).localeCompare(String(a.companyName)),
};

const applyMockQuery = ({
    companyId,
    status,
    dateFrom,
    dateTo,
    hasOriginal,
    sortBy,
    page = 1,
    pageSize = 10,
}) => {
    let items = [...mockRequests];

    if (companyId) items = items.filter((item) => item.companyId === companyId);
    if (status) items = items.filter((item) => item.status === status);
    if (hasOriginal) items = items.filter((item) => Boolean(item.originalSent?.trim()));
    items = items.filter((item) => byDateRange(item.date, dateFrom, dateTo));

    if (sortBy && sorters[sortBy]) {
        items.sort(sorters[sortBy]);
    }

    const total = items.length;
    const offset = (page - 1) * pageSize;

    return {
        items: items.slice(offset, offset + pageSize),
        total,
    };
};

const nowString = () => new Date().toISOString().slice(0, 19).replace("T", " ");

const ApplicationManagementApi = {
    useMock: USE_MOCK_BY_DEFAULT,

    async list(params) {
        if (this.useMock) {
            return applyMockQuery(params || {});
        }
        const res = await http.get("/private/app-document", { params });
        return res.data;
    },

    async create(payload) {
        if (this.useMock) {
            const company = mockCompanies.find((x) => x.id === payload.companyId);
            const created = {
                ...payload,
                id: String(Date.now()),
                companyName: company?.name || payload.companyName,
                updatedAt: nowString(),
            };
            mockRequests = [created, ...mockRequests];
            return created;
        }
        const res = await http.post("/private/app-document/create", payload);
        return res.data;
    },

    async update(id, payload) {
        if (this.useMock) {
            const company = mockCompanies.find((x) => x.id === payload.companyId);
            const next = {
                ...payload,
                id,
                companyName: company?.name || payload.companyName,
                updatedAt: nowString(),
            };
            mockRequests = mockRequests.map((item) => (item.id === id ? next : item));
            return next;
        }
        const res = await http.put(`/private/app-document/${id}`, payload);
        return res.data;
    },

    async remove(id) {
        if (this.useMock) {
            mockRequests = mockRequests.filter((item) => item.id !== id);
            return;
        }
        await http.delete(`/private/app-document/${id}`);
    },

    async listCompanies(query = "") {
        if (this.useMock) {
            const q = query.trim().toLowerCase();
            return mockCompanies.filter((item) => item.name.toLowerCase().includes(q));
        }
        const res = await http.get("/private/company", { params: { q: query, page: 1, pageSize: 100 } });
        return res.data?.items || [];
    },

    async uploadFile(file, onProgress) {
        if (this.useMock) {
            await new Promise((resolve) => {
                let progress = 0;
                const timer = setInterval(() => {
                    progress += 25;
                    onProgress?.({ percent: progress });
                    if (progress >= 100) {
                        clearInterval(timer);
                        resolve(true);
                    }
                }, 120);
            });
            return {
                id: String(Date.now()),
                name: file.name,
                url: "#",
            };
        }

        const data = new FormData();
        data.append("file", file);
        const res = await http.post("/private/app-document/upload", data, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (event) => {
                const percent = event.total ? (event.loaded / event.total) * 100 : 0;
                onProgress?.({ percent });
            },
        });
        return res.data;
    },

    statuses,
};

export default ApplicationManagementApi;
